import React, { useState } from 'react';
import { ArrowRight, Ship, Globe, Award, CheckCircle, Phone, Mail, MapPin, Package, Zap, Shield, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../utils/mock';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-slate-200">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="/icon.png" 
                alt="Feronex Global Logo" 
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold text-slate-800">Feronex Global</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-slate-600 hover:text-slate-900 transition-colors">Home</a>
              <a href="#about" className="text-slate-600 hover:text-slate-900 transition-colors">About</a>
              <a href="#products" className="text-slate-600 hover:text-slate-900 transition-colors">Products</a>
              <a href="#services" className="text-slate-600 hover:text-slate-900 transition-colors">Services</a>
              <a href="#gallery" className="text-slate-600 hover:text-slate-900 transition-colors">Gallery</a>
              <a href="#contact">
                <Button className="bg-slate-700 hover:bg-slate-800">Contact Us</Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-700 hover:text-slate-900 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4 border-t border-slate-200 pt-4">
              <a 
                href="#home" 
                onClick={closeMobileMenu}
                className="block text-slate-600 hover:text-slate-900 transition-colors py-2"
              >
                Home
              </a>
              <a 
                href="#about" 
                onClick={closeMobileMenu}
                className="block text-slate-600 hover:text-slate-900 transition-colors py-2"
              >
                About
              </a>
              <a 
                href="#products" 
                onClick={closeMobileMenu}
                className="block text-slate-600 hover:text-slate-900 transition-colors py-2"
              >
                Products
              </a>
              <a 
                href="#services" 
                onClick={closeMobileMenu}
                className="block text-slate-600 hover:text-slate-900 transition-colors py-2"
              >
                Services
              </a>
              <a 
                href="#gallery" 
                onClick={closeMobileMenu}
                className="block text-slate-600 hover:text-slate-900 transition-colors py-2"
              >
                Gallery
              </a>
              <a href="#contact" onClick={closeMobileMenu}>
                <Button className="w-full bg-slate-700 hover:bg-slate-800">Contact Us</Button>
              </a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center" itemScope itemType="https://schema.org/WebPageElement">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/1624695/pexels-photo-1624695.jpeg" 
            alt="International shipping port - Feronex Global"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/75"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 relative z-10 py-32">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30 rounded-full px-4 py-2 mb-8">
              <Globe className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-300 text-sm font-medium">GLOBAL TRADE & LOGISTICS</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-8" itemProp="headline">
              Global Metal Trading Solutions with Focus on Sustainability
            </h1>
            
            {/* Description */}
            <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed" itemProp="description">
              Feronex Global is an international trade and logistics company specializing in premium stainless steel scrap sourcing and supply worldwide, with primary operations between Europe, UK, and India.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap gap-4 mb-16">
              <a href="#contact">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 shadow-lg">
                  Get in Touch <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href="#services">
                <Button variant="outline" className="text-lg px-8 py-6 border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm">
                  Our Services
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Globe className="w-5 h-5 text-emerald-400" />
                  <p className="text-3xl font-bold text-white">Global</p>
                </div>
                <p className="text-slate-400 text-sm">Trade Network</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white mb-2">Premium</p>
                <p className="text-slate-400 text-sm">Quality Standards</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white mb-2">Sustainable</p>
                <p className="text-slate-400 text-sm">Circular Economy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white" itemScope itemType="https://schema.org/AboutPage">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" itemProp="name">About Feronex Global</h2>
              <p className="text-xl text-emerald-600 font-semibold">Your Trusted Partner in International Metal Trade</p>
            </div>
            
            <div className="space-y-8">
              <p className="text-lg text-slate-700 leading-relaxed text-center" itemProp="description">
                Feronex Global is an international trade and logistics company specializing in the sustainable sourcing and supply of metal scrap, particularly high-grade stainless steel, ferrous and non-ferrous materials. We operate globally with a strong focus on Europe, UK, and India markets, facilitating the circular economy through expert metal recycling and logistics solutions.
              </p>
              
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-full flex items-center justify-center shadow-lg">
                        <Award className="w-12 h-12 text-emerald-600" />
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">Leadership & Vision</h3>
                      <p className="text-lg text-slate-700 leading-relaxed">
                        Under the leadership of <strong>Managing Director Krunal Patel</strong>, we oversee global operations, strategic partnerships, and supply chain management across multiple continents with an unwavering commitment to sustainability, reliability, and excellence in international metal trade.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16 max-w-6xl mx-auto">
            {mockData.about.highlights.map((highlight, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-gradient-to-br from-emerald-100 to-emerald-200 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    {highlight.icon === 'globe' && <Globe className="w-8 h-8 text-emerald-600" />}
                    {highlight.icon === 'award' && <Award className="w-8 h-8 text-emerald-600" />}
                    {highlight.icon === 'shield' && <Shield className="w-8 h-8 text-emerald-600" />}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{highlight.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{highlight.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-slate-50" itemScope itemType="https://schema.org/ItemList">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" itemProp="name">Our Products</h2>
            <p className="text-lg text-slate-600" itemProp="description">
              High-quality scrap metals for India's smelting and metal industry
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.products.map((product, index) => (
              <Card key={index} className="border-slate-200 overflow-hidden hover:shadow-xl transition-all group bg-white">
                <div className="relative overflow-hidden h-64">
                  <img 
                    src={product.image} 
                    alt={`${product.name} - ${product.grades.join(', ')} scrap metal available from Feronex Global`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-2">{product.name}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-slate-600 mb-4">{product.description}</p>
                  
                  {product.gradeTable ? (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-slate-700">Available Grades:</p>
                      {Object.entries(product.gradeTable).map(([series, grades], idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-slate-600 mb-2">{series}</p>
                          <div className="flex flex-wrap gap-2">
                            {grades.map((grade, gIdx) => (
                              <span key={gIdx} className="bg-white text-slate-700 px-3 py-1 rounded-full text-sm border border-slate-200">
                                {grade}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-700">Grades Available:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.grades.map((grade, idx) => (
                          <span key={idx} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                            {grade}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white" itemScope itemType="https://schema.org/Service">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" itemProp="name">Our Services & Capabilities</h2>
            <p className="text-lg text-slate-600" itemProp="description">
              Comprehensive solutions for your scrap metal import needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {mockData.services.map((service, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-slate-700 text-white p-3 rounded-lg flex-shrink-0">
                      {service.icon === 'ship' && <Ship className="w-6 h-6" />}
                      {service.icon === 'globe' && <Globe className="w-6 h-6" />}
                      {service.icon === 'zap' && <Zap className="w-6 h-6" />}
                      {service.icon === 'shield' && <Shield className="w-6 h-6" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{service.title}</h3>
                      <p className="text-slate-600 mb-4">{service.description}</p>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-slate-700 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Supply Network Section */}
      <section className="py-20 bg-gradient-to-br from-slate-700 to-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Globe className="w-16 h-16 mx-auto mb-6 text-emerald-400" />
            <h2 className="text-4xl font-bold mb-6">Global Supply Network</h2>
            <p className="text-xl text-slate-300 mb-12">
              We supply premium scrap metals worldwide with extensive operations across Europe, UK, and India, serving major smelting facilities and metal processing industries globally
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {mockData.supplyNetwork.map((item, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <p className="text-4xl font-bold text-emerald-400 mb-2">{item.value}</p>
                  <p className="text-slate-300">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Feronex Global</h2>
            <p className="text-lg text-slate-600">
              Your trusted global partner for quality scrap metal trade
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.whyChooseUs.map((reason, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="bg-slate-700 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold">{index + 1}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{reason.title}</h3>
                <p className="text-slate-600 text-sm">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Gallery</h2>
            <p className="text-lg text-slate-600">
              Browse through our operations and product quality
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {mockData.gallery.map((image, index) => (
              <div key={index} className="relative group overflow-hidden rounded-xl shadow-lg bg-white">
                <img 
                  src={image.url} 
                  alt={`${image.title} - ${image.category} at Feronex Global scrap metal operations`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg">{image.title}</h3>
                    <p className="text-slate-300 text-sm">{image.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white" itemScope itemType="https://schema.org/ContactPage">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-slate-900 mb-6" itemProp="name">
                Get in Touch
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto" itemProp="description">
                Ready to discuss your scrap metal requirements? Reach out to us through any of these channels
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {/* Email Card */}
              <Card className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-2 group bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-slate-100 group-hover:bg-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 shadow-lg">
                    <Mail className="w-10 h-10 text-slate-700 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-xl mb-3">Email Us</h3>
                  <a 
                    href="mailto:krunal@feronexglobal.com" 
                    className="text-slate-600 hover:text-slate-900 transition-colors text-lg font-medium block break-all" 
                    itemProp="email"
                  >
                    krunal@feronexglobal.com
                  </a>
                </CardContent>
              </Card>
              
              {/* Phone Card */}
              <Card className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-2 group bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-slate-100 group-hover:bg-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 shadow-lg">
                    <Phone className="w-10 h-10 text-slate-700 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-xl mb-3">Call Us</h3>
                  <a 
                    href="tel:+447565482365" 
                    className="text-slate-600 hover:text-slate-900 transition-colors text-lg font-medium block" 
                    itemProp="telephone"
                  >
                    +44 7565 482365
                  </a>
                  <p className="text-slate-500 text-sm mt-3">Mon-Fri, 9 AM - 6 PM GMT</p>
                </CardContent>
              </Card>
              
              {/* WhatsApp Card */}
              <Card className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-2 group bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-slate-100 group-hover:bg-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 shadow-lg">
                    <MessageCircle className="w-10 h-10 text-slate-700 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-xl mb-3">WhatsApp</h3>
                  <a 
                    href="https://wa.me/447565482365" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-900 transition-colors text-lg font-medium block"
                  >
                    +44 7565 482365
                  </a>
                  <p className="text-slate-500 text-sm mt-3">Quick messaging</p>
                </CardContent>
              </Card>
              
              {/* Address Card */}
              <Card className="border-slate-200 hover:shadow-xl transition-all hover:-translate-y-2 group bg-white">
                <CardContent className="p-8 text-center" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                  <div className="bg-slate-100 group-hover:bg-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 shadow-lg">
                    <MapPin className="w-10 h-10 text-slate-700 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-xl mb-3">Visit Us</h3>
                  <p className="text-slate-600 text-base leading-relaxed">
                    <span itemProp="streetAddress" className="block">205 Crescent Rd</span>
                    <span itemProp="addressLocality" className="block">Barnet, London</span>
                    <span itemProp="addressCountry" className="block">United Kingdom</span>
                    <span itemProp="postalCode" className="block font-semibold mt-1">EN4 8SB</span>
                  </p>
                  <p className="text-slate-500 text-sm mt-3">By appointment only</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Additional CTA */}
            <div className="text-center">
              <Card className="inline-block border-slate-200 shadow-lg max-w-2xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Need a Quote?</h3>
                  <p className="text-slate-600 mb-6">
                    Contact us with your requirements and we'll get back to you with a competitive quote within 24 hours
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <a href="mailto:krunal@feronexglobal.com">
                      <Button className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                        <Mail className="w-5 h-5 mr-2" />
                        Email for Quote
                      </Button>
                    </a>
                    <a href="tel:+447565482365">
                      <Button variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg">
                        <Phone className="w-5 h-5 mr-2" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" role="contentinfo">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_metalbridge-1/artifacts/6jrnt8u2_image.png" 
                  alt="Feronex Global Logo" 
                  className="w-10 h-10"
                />
                <span className="text-xl font-bold">Feronex Global</span>
              </div>
              <p className="text-slate-400">
                Your trusted partner for premium scrap metal trade worldwide.
              </p>
            </div>
            
            <nav>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#home" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#products" className="hover:text-white transition-colors">Products</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
              </ul>
            </nav>
            
            <nav>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-slate-400">
                <li>Stainless Steel (SS304)</li>
                <li>Stainless Steel (SS316)</li>
                <li>Ferrous Metals</li>
                <li>Non-Ferrous Metals</li>
              </ul>
            </nav>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="mailto:krunal@feronexglobal.com" className="hover:text-white transition-colors">krunal@feronexglobal.com</a></li>
                <li><a href="tel:+447565482365" className="hover:text-white transition-colors">+44 7565 482365</a></li>
                <li>Barnet, London, United Kingdom</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2025 Feronex Global. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
